import { IRadioOption, RadioButton } from '@aon/aon-tws-common-ui/build/lib/components/Common/RadioButton';
import * as React from 'react';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { QuestionType, ResponseOptionGroupViewModel } from '../../../models';
import { Col2Col8Col2d, ColFull, ColHalves, errorText } from '../../common';
import { ResponseOptionsByOrderedItemTooltipContent, ResponseOptionsTooltipContent } from './ResponseOptionsTooltipContent';

export interface IResponseOptionGroupRadioListProps {
	errorMsg?: string;
	hasBeenSaved: boolean;
	questionType: QuestionType;
	responseOptionGroups: ResponseOptionGroupViewModel[];
	selected?: number;
	onChange: (responseOptionGroupId: number) => void;
	setOpenEnded: () => void;
	setTextBox: () => void;
}

export const ErrorText = styled.p`
	${errorText}
`;
ErrorText.displayName = 'ErrorText';

export interface IResponseOptionGroupItem {
	value: number;
	name: string;
	displayText: string;
	toolTip?: JSX.Element;
}

export const OpenEndedRadioButton = styled(RadioButton).attrs<{}>({
	className: 'tws-custom-question--open-ended-response-option',
})``;

export const openEndedOption: IResponseOptionGroupItem = {
	value: 0,
	name: 'open-ended',
	displayText: ContentConstants.RESPONSE_OPTION_GROUP_OPENENDED,
	toolTip: ResponseOptionsTooltipContent(
		ContentConstants.RESPONSE_OPTION_GROUP_OPENENDED,
		[],
		ContentConstants.RESPONSE_OPTION_GROUP_OPENENDED_DESCRIPTION,
	),
};

export const TextBoxRadioButton = styled(RadioButton).attrs<{}>({
	className: 'tws-custom-question--text-box-response-option',
})``;

export const textBoxOption: IResponseOptionGroupItem = {
	value: 0,
	name: 'text-box',
	displayText: ContentConstants.RESPONSE_OPTION_GROUP_TEXTBOX,
	toolTip: ResponseOptionsTooltipContent(
		ContentConstants.RESPONSE_OPTION_GROUP_TEXTBOX,
		[],
		ContentConstants.RESPONSE_OPTION_GROUP_TEXTBOX_DESCRIPTION,
	),
};

export class ResponseOptionGroupRadioList extends React.Component<React.PropsWithChildren<IResponseOptionGroupRadioListProps>> {
	public render() {
		const html: JSX.Element[] = [];

		const responseOptionGroups: IResponseOptionGroupItem[] = this.getResponseOptionGroups();
		const radioButtons = this.getRadioButtons(responseOptionGroups);

		if (radioButtons.length > 0) {
			const children = this.getRadioButtonColumns(radioButtons);

			if (this.props.errorMsg !== undefined && this.props.errorMsg !== '') {
				children.push(
					<ColFull key={responseOptionGroups.length + 1}>
						<ErrorText>{this.props.errorMsg}</ErrorText>
					</ColFull>,
				);
			}

			html.push(<Col2Col8Col2d key='1'>{children}</Col2Col8Col2d>);
		}

		return html;
	}

	private getRadioButtons(responseOptionGroups: IResponseOptionGroupItem[]) {
		const { hasBeenSaved, selected, questionType } = this.props;
		const radioButtons = responseOptionGroups.map((item: IResponseOptionGroupItem) => {
			const disabled = hasBeenSaved && questionType !== QuestionType.Scale;
			const isChecked = questionType !== QuestionType.OpenEnded && questionType !== QuestionType.TextBox && selected === item.value;
			const radioOption: IRadioOption = { ...item };
			return (
				<RadioButton
					groupName='customResponseOptionGroups'
					isChecked={isChecked}
					key={item.value}
					size='lg'
					disabled={disabled}
					item={radioOption}
					handleChange={() => disabled || this.props.onChange(item.value)}
					showTooltips={true}
				/>
			);
		});

		if (radioButtons.length > 0) {
			const disableOpenEnded = hasBeenSaved && questionType !== QuestionType.OpenEnded;
			const disableTextBox = hasBeenSaved && questionType !== QuestionType.TextBox;
			const openEndedButton = (
				<OpenEndedRadioButton
					groupName='customResponseOptionGroups'
					isChecked={questionType === QuestionType.OpenEnded}
					key={'open-ended'}
					size='lg'
					disabled={disableOpenEnded}
					item={openEndedOption}
					handleChange={() => disableOpenEnded || this.props.setOpenEnded()}
					showTooltips={true}
				/>
			);
			radioButtons.push(openEndedButton);
			const textBoxButton = (
				<TextBoxRadioButton
					groupName='customResponseOptionGroups'
					isChecked={questionType === QuestionType.TextBox}
					key={'text-box'}
					size='lg'
					disabled={disableTextBox}
					item={textBoxOption}
					handleChange={() => disableTextBox || this.props.setTextBox()}
					showTooltips={true}
				/>
			);
			radioButtons.push(textBoxButton);
		}

		return radioButtons;
	}

	private getRadioButtonColumns(radioButtons: JSX.Element[]) {
		let col1: JSX.Element = { type: '', props: '', key: '' };
		let col2: JSX.Element;
		const children: JSX.Element[] = [];

		for (let i = 0; radioButtons.length > i; i++) {
			if (i % 2 > 0) {
				col2 = radioButtons[i];
				children.push(<ColHalves key={i} Col1={col1} Col2={col2} />);
			} else {
				col1 = radioButtons[i];

				// If there is an odd number of radio buttons, the last one needs to get pushed separately.
				if (i === radioButtons.length - 1) {
					children.push(<ColHalves key={i} Col1={col1} Col2={undefined} />);
				}
			}
		}
		return children;
	}

	private sortResponseGroups(viewModels: ResponseOptionGroupViewModel[]) {
		let respGrp: ResponseOptionGroupViewModel | undefined;
		const sortedViewModels: ResponseOptionGroupViewModel[] = [];

		const additionalRogDisplayTexts: string[] = [
			ContentConstants.RESPONSE_OPTION_GROUP_TEN_POINT,
			ContentConstants.RESPONSE_OPTION_GROUP_SEVEN_POINT_AGREE,
			ContentConstants.RESPONSE_OPTION_GROUP_SEVEN_POINT_SATISFACTION,
			ContentConstants.RESPONSE_OPTION_GROUP_THREE_POINT_MANAGER_SUPPORT,
		];
		const additionalRogCodes: string[] = this.props.responseOptionGroups
			.filter((rog) => additionalRogDisplayTexts.includes(rog.displayText))
			.map((rog) => rog.code);
		const codesToShow = Constants.CUSTOMQUESTION_RESPONSEOPTIONGROUP_CODES.concat(additionalRogCodes);

		codesToShow.forEach((code) => {
			respGrp = viewModels.find((vm) => vm.code === code);
			if (respGrp !== undefined) {
				sortedViewModels.push(respGrp);
			}
		});
		return sortedViewModels;
	}

	private getResponseOptionGroups(): IResponseOptionGroupItem[] {
		const orderedResponseOptionGroups = this.sortResponseGroups(this.props.responseOptionGroups);

		const responseOptionGroups = orderedResponseOptionGroups.map((responseOptionGroup: ResponseOptionGroupViewModel) => {
			const toolTip =
				responseOptionGroup.responseOptions === undefined || !responseOptionGroup.responseOptions.length
					? undefined
					: ResponseOptionsByOrderedItemTooltipContent(responseOptionGroup.displayText, responseOptionGroup.responseOptions);

			return {
				value: responseOptionGroup.responseOptionGroupId,
				name: responseOptionGroup.displayText,
				displayText: responseOptionGroup.displayText,
				toolTip,
			};
		});
		return responseOptionGroups;
	}
}
